<template>
  <main id="main-content">
    <section v-if="submitPolicyInProcess">
      <h1>{{ submitPolicyInProcessHeader }}</h1>
      <p v-html="ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER"></p>
    </section>
    <SuccessBanner v-if="!submitPolicyInProcess" header="Your rebate update was successful">
      <template>
        <div>
          <p v-html="bannerSubText" />
        </div>
      </template>
    </SuccessBanner>
    <div v-if="!submitPolicyInProcess" class="page-content">
      <h2>Your premium information</h2>
      <ProductSummaryCard>
        <template #left>
          <h3>{{ membershipInfo.productName }}</h3>
          <ul>
            <li v-if="newRebateTier">Rebate tier - {{ RebateTierDisplayWithoutIncomeBrackets[newRebateTier] }}</li>
            <li>Effective from {{ convertDateStringToText(getCurrentDate()) }}</li>
            <li v-if="premiumTimelineData[1].date">Next payment due {{ convertDateStringToText(premiumTimelineData[1].date) }}</li>
          </ul>
          <div class="margin-top" v-if="premiumChangeInformationText" v-html="premiumChangeInformationText" />
          <div class="margin-top" v-else>
            <h6>How your premium has changed</h6>
            <p>Your premium has been updated based on your requested rebate tier.</p>
          </div>
        </template>
        <template #right>
          <h3>Your premium</h3>
          <p class="text-green bold no-margin" v-if="newPremium && currentPayFrequency">
            <span class="font-large">{{ formatAsCurrency(newPremium) }}</span>
            &nbsp;/&nbsp;{{ mapPaymentFrequencyFromRecurringToSingleton(currentPayFrequency) }}
          </p>
        </template>
      </ProductSummaryCard>

      <ApiLoading
        class="api-loading"
        v-if="apiStatus.premiumAdjustmentTimeline.loading || apiStatus.premiumAdjustmentTimeline.fail"
        :apiError="apiStatus.premiumAdjustmentTimeline.fail"
      />
      <PremiumAdjustmentTimeline v-else :refData="refData" :premiumTimelineData="premiumTimelineData" @handlePayNowBtnClick="handlePayNowBtnClick" />
    </div>
  </main>
</template>

<script>
import { SuccessBanner, ProductSummaryCard, PremiumAdjustmentTimeline, ApiLoading } from '@/components';
import { RebateTierDisplayWithoutIncomeBrackets } from '@/enums';
import { getCurrentDate, convertDateStringToText } from '@general/js/utils';
import PremiumImpactServices from '@/services/PremiumImpact';
import { ErrorStrings } from '@general/js/constants';
import { MY_COVER_MAKE_A_PAYMENT, REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import RebateMixins from './RebateMixins';

export default {
  name: 'RebateUpdated',
  components: { SuccessBanner, ProductSummaryCard, PremiumAdjustmentTimeline, ApiLoading },
  mixins: [RebateMixins],
  props: {
    bannerSubText: {
      type: String,
      default: '',
    },
    newPremium: {
      type: Number,
    },
    currentPayFrequency: {
      type: String,
    },
    newRebateTier: {
      type: String,
      default: '',
    },
    premiumChangeInformationText: {
      type: String,
      default: '',
    },
    premiumAdjustmentInformationText: {
      type: String,
      default: null,
    },
    submitPolicyInProcessHeader: {
      type: String,
      default: '',
    },
    submitPolicyInProcessSubHeader: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      apiStatus: {
        premiumAdjustmentTimeline: {
          loading: false,
          fail: false,
        },
      },
      refData: {
        paymentAdjustmentInformationText: {
          informationText:
            this.premiumAdjustmentInformationText ||
            'Any changes to your premium will be automatically adjusted in your next bill. Alternatively, you can pay for the remaining amount due now.',
        },
      },
      premiumTimelineData: [
        {
          paymentAmount: null,
          label: 'Difference to amount paid',
        },
        {
          paymentAmount: null,
          label: 'Next payment',
          date: null,
        },
        {
          paymentAmount: null,
          label: 'Payment onwards',
          date: null,
        },
      ],
      RebateTierDisplayWithoutIncomeBrackets,
      ErrorStrings,
    };
  },

  beforeMount() {
    this.getPremiumAdjustmentPrices();
  },

  methods: {
    async getPremiumAdjustmentPrices() {
      this.apiStatus.premiumAdjustmentTimeline.loading = true;

      try {
        this.premiumAdjustmentPrices = await PremiumImpactServices.getPremiumAdjustmentPrices(this.memberNumber, true);
        this.premiumTimelineData[0].paymentAmount = this.premiumAdjustmentPrices.prorataPrice;

        this.premiumTimelineData[1].paymentAmount = this.newPremium ? this.newPremium : this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
        this.premiumTimelineData[2].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;

        this.premiumTimelineData[1].date = this.premiumAdjustmentPrices.nextContributionDate;
        this.premiumTimelineData[2].date = this.premiumAdjustmentPrices.futureContributionDate;
      } catch (err) {
        logger.error(err);
        this.apiStatus.premiumAdjustmentTimeline.fail = true;
      } finally {
        this.apiStatus.premiumAdjustmentTimeline.loading = false;
      }
    },

    handlePayNowBtnClick(amt) {
      setNavigationState({
        paymentAmount: amt,
        routeParams: { homePage: REBATES_MEMBER_PORTAL.REBATE_OVERVIEW },
      });
      this.$router.push({
        name: MY_COVER_MAKE_A_PAYMENT,
      });
    },

    getCurrentDate,
    convertDateStringToText,
  },
};
</script>

<style lang="scss" scoped></style>
